import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SelectHsn, SelectUomConversion, SelectRawItems, SelectStatus } from "components/ui/selectors";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ActionButton,
} from "components/ui/Table";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import Modal from "components/ui/modals";
import DeleteItemDetail from "components/itemBulk/DeleteItemDetail";
import EditItemDetail from "components/itemBulk/EditItemDetail";

import { useOneAgitem, useUpdateAgitem } from "http-hooks/agitem/useAgitem";
import Loading from "components/Loading";
import useCompanyPreferences from "hooks/useCompanyPreferences";

export default function EditItemBulk({ id }) {
  const navigate = useNavigate();
  const [deleteItemDetailId, setDeleteItemDetailId] = useState(null);
  const [editItemDetailId, setEditItemDetailId] = useState(null);

  // Get company preferences to conditionally show fields
  const {
    isShapeEnabled,
    isColorEnabled,
    isSizeEnabled,
    isForeignExportEnabled,
  } =
    useCompanyPreferences();
  const isTaxClassificationOptional = isForeignExportEnabled();

  // Combined state for selected values
  const [selected, setSelected] = useState({
    itmhsnId: null,
    selectedRawItem: null,
    uomConval: null,
    status: null,
  });

  // Filter state for table
  const [filters, setFilters] = useState({
    shape: null,
    color: null,
    size: null,
  });

  const { isLoading, data: agItem } = useOneAgitem(id, !!id);

  // Set initial values when agItem loads
  useEffect(() => {
    if (agItem) {
      console.log("Setting initial values from agItem:", agItem);
      
      // Check if itemCode or itemName exist and are not empty strings
      const hasItemCode = agItem.itemCode && agItem.itemCode.trim() !== "";
      const hasItemName = agItem.itemName && agItem.itemName.trim() !== "";
      
      setSelected({
        itmhsnId: agItem.itmhsnId ? { hsncode: agItem.itmhsnId, label: agItem.itmhsnId } : null,
        selectedRawItem: hasItemName
          ? { 
              item_code: hasItemCode ? agItem.itemCode : "", 
              item_name: agItem.itemName 
            }
          : null,
        uomConval: agItem.itemuom && agItem.itmbasicuom && agItem.aguomconval
          ? { itemuom: agItem.itemuom, itembasicuom: agItem.itmbasicuom, conval: agItem.aguomconval }
          : null,
        status: agItem.ag_status !== undefined
          ? { value: agItem.ag_status, label: agItem.ag_status === 1 ? "Active" : "Inactive" }
          : null,
      });
    }
  }, [agItem]);

  // Reset state when component unmounts or id changes
  useEffect(() => {
    return () => {
      // Cleanup: reset state when navigating away
      setSelected({
        itmhsnId: null,
        selectedRawItem: null,
        uomConval: null,
        status: null,
      });
    };
  }, [id]);

  // Helper functions to get unique values from agitemDetails
  const getUniqueShapes = useMemo(() => {
    if (!agItem?.agitemDetails) return [];
    const shapes = [...new Set(agItem.agitemDetails.map(item => item.shp).filter(Boolean))];
    return shapes.map(shape => ({ value: shape, label: shape }));
  }, [agItem]);

  const getUniqueColors = useMemo(() => {
    if (!agItem?.agitemDetails) return [];
    const colors = [...new Set(agItem.agitemDetails.map(item => item.clt).filter(Boolean))];
    return colors.map(color => ({ value: color, label: color }));
  }, [agItem]);

  const getUniqueSizes = useMemo(() => {
    if (!agItem?.agitemDetails) return [];
    const sizes = [...new Set(agItem.agitemDetails.map(item => `${item.sz} ${item.szuom}`.trim()).filter(Boolean))];
    return sizes.map(size => ({ value: size, label: size }));
  }, [agItem]);

  // Filter the agitemDetails based on current filters
  const getFilteredCombinations = useMemo(() => {
    if (!agItem?.agitemDetails) return [];
    
    return agItem.agitemDetails.filter(combination => {
      const shapeMatch = !filters.shape || combination.shp === filters.shape.value;
      const colorMatch = !filters.color || combination.clt === filters.color.value;
      const sizeMatch = !filters.size || `${combination.sz} ${combination.szuom}`.trim() === filters.size.value;
      
      return shapeMatch && colorMatch && sizeMatch;
    });
  }, [agItem, filters]);

  const { mutateAsync: mutateUpdate, isPending: isAgitemUpdating } = useUpdateAgitem();

  const handleUpdate = async () => {
    try {
      const itemData = {
        itemName: selected.selectedRawItem?.item_name,
        itemCode: selected.selectedRawItem?.item_code,
        itmhsnId: selected.itmhsnId?.hsncode,
        itemuom: selected.uomConval?.itemuom,
        itmbasicuom: selected.uomConval?.itembasicuom,
        aguomconval: selected.uomConval?.conval,
        ag_status: selected.status?.value,
      };

      console.log("Updating item:", itemData);

      await mutateUpdate({ id, data: itemData });
      // Item updated successfully, navigate back to items list
      navigate('/item-master-bulk');
    } catch (error) {
      // Error toast already shown by service
      console.error("Update failed:", error);
    }
  };

  const handleEditCombination = (combination) => {
    setEditItemDetailId(combination.agitemdtlId);
  };

  const handleDeleteCombination = (combination) => {
    setDeleteItemDetailId(combination.agitemdtlId);
  };

  // Show loading state if no id provided
  if (!id) {
    return <Loading />;
  }

  // Show loading state early
  if (isLoading && !agItem) {
    return <Loading />;
  }

  return (
    <>
      {deleteItemDetailId && (
        <Modal
          open={!!deleteItemDetailId}
          setOpen={(val) => !val && setDeleteItemDetailId(null)}
        >
          <DeleteItemDetail
            id={deleteItemDetailId}
            onClose={() => setDeleteItemDetailId(null)}
          />
        </Modal>
      )}

      {editItemDetailId && (
        <Modal
          open={!!editItemDetailId}
          setOpen={(val) => !val && setEditItemDetailId(null)}
        >
          <EditItemDetail
            id={editItemDetailId}
            onClose={() => setEditItemDetailId(null)}
          />
        </Modal>
      )}

      <form className="space-y-8">
        {/* Item Information Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SelectRawItems
            value={selected.selectedRawItem}
            onChange={(selectedOption) =>
              setSelected((prev) => ({
                ...prev,
                selectedRawItem: selectedOption,
              }))
            }
            placeholder="Search raw items..."
            isRequired={true}
            isDisabled={false}
            label="Raw Item"
          />

          <Input
            label="Sub-brand"
            value={agItem?.subbrandname || ""}
            readOnly
          />
        </div>

        {/* Item Configuration Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SelectHsn
            value={selected.itmhsnId}
            onChange={(selectedOption) =>
              setSelected((prev) => ({
                ...prev,
                itmhsnId: selectedOption,
              }))
            }
            label="HSN / SAC"
            placeholder="Search HSN or SAC..."
            isRequired={!isTaxClassificationOptional}
            isDisabled={false}
          />

          <SelectUomConversion
            value={selected.uomConval}
            onChange={(selectedOption) => setSelected((prev) => ({
              ...prev,
              uomConval: selectedOption,
            }))}
            placeholder="Search conversion..."
            isRequired={true}
            isDisabled={false}
          />

          <Input
            label="Unit of Measurement"
            value={agItem?.itmastuom || ""}
            readOnly
          />

          <div>
            <SelectStatus
              value={selected.status}
              onChange={(selectedOption) =>
                setSelected((prev) => ({
                  ...prev,
                  status: selectedOption,
                }))
              }
              placeholder="Select status"
            />
            {agItem?.ag_status === 1 && selected.status?.value === 0 && (
              <p className="mt-1 text-sm text-amber-600">
                ⚠️ This will also make all related item details inactive
              </p>
            )}
          </div>
        </div>

        {/* Item Combinations Table */}
        {agItem && (
          <>
            {/* Filters */}
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Filter Item Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {isShapeEnabled() && (
                  <Select
                    label="Filter by Shape"
                    value={filters.shape}
                    onChange={(selectedOption) =>
                      setFilters((prev) => ({
                        ...prev,
                        shape: selectedOption,
                      }))
                    }
                    options={getUniqueShapes}
                    placeholder="All Shapes"
                    isClearable={true}
                    isSearchable={false}
                  />
                )}

                {isColorEnabled() && (
                  <Select
                    label="Filter by Color"
                    value={filters.color}
                    onChange={(selectedOption) =>
                      setFilters((prev) => ({
                        ...prev,
                        color: selectedOption,
                      }))
                    }
                    options={getUniqueColors}
                    placeholder="All Colors"
                    isClearable={true}
                    isSearchable={false}
                  />
                )}

                {isSizeEnabled() && (
                  <Select
                    label="Filter by Size"
                    value={filters.size}
                    onChange={(selectedOption) =>
                      setFilters((prev) => ({
                        ...prev,
                        size: selectedOption,
                      }))
                    }
                    options={getUniqueSizes}
                    placeholder="All Sizes"
                    isClearable={true}
                    isSearchable={false}
                  />
                )}
              </div>
            </div>

            <Table>
              <THead>
                <TR>
                  <TH>Item Detail ID</TH>
                  {isShapeEnabled() && <TH>Shape</TH>}
                  {isColorEnabled() && <TH>Color</TH>}
                  {isSizeEnabled() && <TH>Size</TH>}
                  {isSizeEnabled() && <TH>Size Type</TH>}
                  {isSizeEnabled() && <TH>Size Group</TH>}
                  <TH>Pack Ratio</TH>
                  <TH>Status</TH>
                  <TH last>
                    <span className="sr-only">Actions</span>
                  </TH>
                </TR>
              </THead>
              <TBody>
                {getFilteredCombinations.length === 0 ? (
                  <TR>
                    <TD
                      colSpan={
                        4 +
                        (isShapeEnabled() ? 1 : 0) +
                        (isColorEnabled() ? 1 : 0) +
                        (isSizeEnabled() ? 3 : 0)
                      }
                      className="text-center text-gray-500"
                    >
                      {agItem.agitemDetails?.length === 0 
                        ? "No item combinations found."
                        : "No item combinations match the selected filters."}
                    </TD>
                  </TR>
                ) : (
                  getFilteredCombinations.map((combination, index) => (
                    <TR key={combination.agitemdtlId || index}>
                      <TD>{combination.agitemdtlId}</TD>
                      {isShapeEnabled() && <TD>{combination.shp}</TD>}
                      {isColorEnabled() && <TD>{combination.clt}</TD>}
                      {isSizeEnabled() && (
                        <TD>
                          {combination.sz} {combination.szuom}
                        </TD>
                      )}
                      {isSizeEnabled() && <TD>{combination.itmsztyp}</TD>}
                      {isSizeEnabled() && (
                        <TD>
                          {combination.sizeGrouping ? (
                            <span className="text-xs text-indigo-700 font-medium">
                              {combination.sizeGrouping.name}
                              {combination.sizeGrouping.size_uom ? ` (${combination.sizeGrouping.size_uom})` : ""}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </TD>
                      )}
                      <TD>{combination.packratio}</TD>
                      <TD>
                        {combination.ag_status === 1 ? (
                          <CheckIcon
                            className="h-5 w-5 text-green-600"
                            aria-hidden="true"
                          />
                        ) : (
                          <XMarkIcon
                            className="h-5 w-5 text-red-600"
                            aria-hidden="true"
                          />
                        )}
                      </TD>
                      <TD last>
                        <ActionButton
                          variant="secondary"
                          onClick={() => handleEditCombination(combination)}
                          title="Edit Combination"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </ActionButton>
                        <ActionButton
                          variant="danger"
                          onClick={() => handleDeleteCombination(combination)}
                          title="Delete Combination"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </ActionButton>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </>
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate("/item-master-bulk")}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            disabled={isAgitemUpdating}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isAgitemUpdating}
            onClick={handleUpdate}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isAgitemUpdating && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>{isAgitemUpdating ? "Updating..." : "Update"}</span>
          </button>
        </div>
      </form>
    </>
  );
}
